import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import styled from "styled-components";
import {
  DateTableCell,
  ReactTableComponent,
  Status,
  Typography,
  TypographyVariant,
} from "../../../components/ui-kit";
import api from "../../../util/api";
import { DecideApprovalModal } from "./DecideApprovalModal";

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
`;

const FilterBar = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
`;

const SearchInput = styled.input`
  padding: 6px 12px;
  border-radius: 5px;
  border: 1px solid #dbe3eb;
  min-width: 200px;
`;

interface ApprovalRow {
  _id: string;
  status: string;
  approvedProviders?: string[] | null;
  defaultProvider?: string | null;
  riskAssessment: string | null;
  rejectionReason: string | null;
  adminNotes: string | null;
  requestedAt: string;
  decidedAt: string | null;
  company?: { _id: string; tradingName?: string; businessName?: string };
}

const statusMap: Record<string, "completed" | "pending" | "rejected"> = {
  APPROVED: "completed",
  PENDING: "pending",
  REJECTED: "rejected",
};

export const CardPaymentApprovalsList: React.FC = () => {
  const [data, setData] = useState<ApprovalRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [pagination, setPagination] = useState({ page: 1, limit: 25 });
  const [totalDocs, setTotalDocs] = useState(0);
  const [selectedApproval, setSelectedApproval] = useState<ApprovalRow | null>(
    null
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api.getCardPaymentApprovals({
        page: pagination.page,
        limit: pagination.limit,
        ...(statusFilter ? { status: statusFilter } : {}),
        ...(debouncedSearch ? { searchKeyword: debouncedSearch } : {}),
      });
      setData(result?.docs ?? []);
      setTotalDocs(result?.totalDocs ?? 0);
    } catch {
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [pagination, statusFilter, debouncedSearch]);

  // Each keystroke otherwise fires its own two-stage aggregation.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const columns: ColumnDef<ApprovalRow, any>[] = useMemo(
    () => [
      {
        accessorKey: "company",
        header: "Company",
        cell: ({ getValue }) => {
          const c = getValue() as ApprovalRow["company"];
          return c?.tradingName || c?.businessName || "—";
        },
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ getValue }) => {
          const status = getValue() as string;
          return (
            <Status status={statusMap[status] || status}>
              {status.replace(/_/g, " ")}
            </Status>
          );
        },
      },
      {
        accessorKey: "approvedProviders",
        header: "Providers",
        cell: ({ row, getValue }) => {
          const vals = (getValue() as string[] | null) ?? [];
          if (vals.length === 0) return "—";
          const fallback = row.original.defaultProvider;
          return vals
            .map((v) => {
              const name = v.charAt(0).toUpperCase() + v.slice(1);
              return vals.length > 1 && v === fallback
                ? `${name} (default)`
                : name;
            })
            .join(", ");
        },
      },
      {
        accessorKey: "riskAssessment",
        header: "Risk",
        cell: ({ getValue }) => {
          const val = getValue() as string | null;
          if (!val) return "—";
          const colorMap: Record<string, string> = {
            LOW: "#16a34a",
            MEDIUM: "#d97706",
            HIGH: "#dc2626",
          };
          return (
            <span
              style={{ color: colorMap[val] || "inherit", fontWeight: 600 }}
            >
              {val}
            </span>
          );
        },
      },
      {
        accessorKey: "requestedAt",
        header: "Requested",
        cell: ({ getValue }) => <DateTableCell date={getValue()} />,
      },
      {
        accessorKey: "decidedAt",
        header: "Decided",
        cell: ({ getValue }) =>
          getValue() ? <DateTableCell date={getValue()} /> : "—",
      },
    ],
    []
  );

  return (
    <Wrap>
      <Typography variant={TypographyVariant.HEADER1}>
        Card Payment Approvals
      </Typography>

      <FilterBar>
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPagination((p) => ({ ...p, page: 1 }));
          }}
          style={{
            padding: "6px 12px",
            borderRadius: 5,
            border: "1px solid #dbe3eb",
          }}
        >
          <option value="">All statuses</option>
          <option value="PENDING">Pending</option>
          <option value="APPROVED">Approved</option>
          <option value="REJECTED">Rejected</option>
        </select>
        <SearchInput
          placeholder="Search by company name..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPagination((p) => ({ ...p, page: 1 }));
          }}
        />
        <Typography
          variant={TypographyVariant.BODY7}
          style={{ color: "#6b7280" }}
        >
          {totalDocs} request{totalDocs !== 1 ? "s" : ""}
        </Typography>
      </FilterBar>

      <ReactTableComponent
        data={data}
        columns={columns}
        loading={loading}
        onRowClick={(row: ApprovalRow) => setSelectedApproval(row)}
        emptyMessage="No card payment approval requests"
        withPagination={{
          totalDocs,
          page: pagination.page,
          limit: pagination.limit,
          onPaginationChange: (nextPage: number, nextLimit: number) => {
            setPagination({ page: nextPage, limit: nextLimit });
          },
        }}
      />

      {selectedApproval && (
        <DecideApprovalModal
          approval={selectedApproval}
          onClose={() => setSelectedApproval(null)}
          onDecided={() => {
            setSelectedApproval(null);
            fetchData();
          }}
        />
      )}
    </Wrap>
  );
};
